import React, { useEffect, useRef, useState } from "react";
import {
  ArrowRight,
  BriefcaseBusiness,
  CheckCircle2,
  ClipboardCheck,
  GraduationCap,
  Handshake,
  ShieldCheck,
  Store,
} from "lucide-react";
import { Link } from "react-router-dom";
import PageMeta from "../components/PageMeta";
import RegistrationModal from "../components/RegistrationModal";
import PaymentFlowSection from "../components/business/PaymentFlowSection";
import { useLanguage } from "../i18n/LanguageProvider";

const profiles = [
  {
    icon: GraduationCap,
    title: "Écoles et établissements",
    text: "Structurez votre demande autour du paiement et du suivi des frais scolaires.",
  },
  {
    icon: Store,
    title: "Marchands et commerces",
    text: "Présentez votre activité, vos points de vente et vos besoins d’encaissement.",
  },
  {
    icon: BriefcaseBusiness,
    title: "Prestataires de services",
    text: "Décrivez vos abonnements, cotisations, forfaits ou paiements par prestation.",
  },
];

const benefits = [
  {
    icon: ClipboardCheck,
    title: "Une demande adaptée à votre profil",
    text: "Les questions évoluent selon votre activité pour recueillir uniquement les informations pertinentes.",
  },
  {
    icon: ShieldCheck,
    title: "Un parcours clair et sécurisé",
    text: "Vous vérifiez toutes les informations avant leur transmission à l’équipe WonyaSoft.",
  },
  {
    icon: Handshake,
    title: "Une prochaine étape mieux préparée",
    text: "Votre brief permet à notre équipe de comprendre le contexte avant de vous recontacter.",
  },
];

const Business = () => {
  const { t } = useLanguage();
  const [isRegistrationOpen, setIsRegistrationOpen] = useState(false);
  const registrationTriggerRef = useRef(null);

  const openRegistration = (event) => {
    registrationTriggerRef.current = event.currentTarget;
    setIsRegistrationOpen(true);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="ws-page ws-business" data-no-i18n="true">
      <PageMeta
        title="WonyaSoft Business | Inscription école, marchand et prestataire"
        description="Inscrivez votre école, commerce ou activité de services et présentez vos besoins de paiement à l’équipe WonyaSoft."
      />

      <section className="ws-business-hero">
        <div className="ws-shell ws-business-hero__grid">
          <div className="ws-business-hero__copy">
            <span className="ws-eyebrow">
              <BriefcaseBusiness size={18} aria-hidden="true" /> WonyaSoft
              Business
            </span>
            <h1>{t("Des paiements mieux adaptés à votre activité")}</h1>
            <p>
              {t(
                "École, commerce ou activité de services : présentez votre organisation et préparez avec WonyaSoft un parcours de paiement adapté à votre fonctionnement.",
              )}
            </p>
            <div className="ws-button-row">
              <button
                className="ws-button ws-button--primary"
                type="button"
                ref={registrationTriggerRef}
                onClick={openRegistration}
                aria-haspopup="dialog"
              >
                {t("Commencer l’inscription")}{" "}
                <ArrowRight size={18} aria-hidden="true" />
              </button>
              <Link className="ws-button ws-button--secondary" to="/contact">
                {t("Parler à notre équipe")}{" "}
                <ArrowRight size={18} aria-hidden="true" />
              </Link>
            </div>
            <div
              className="ws-business-hero__assurance"
              aria-label={t("Points clés du parcours Business")}
            >
              <span>
                <CheckCircle2 size={17} aria-hidden="true" />{" "}
                {t("Questions adaptées au profil")}
              </span>
              <span>
                <ShieldCheck size={17} aria-hidden="true" />{" "}
                {t("Vérification avant envoi")}
              </span>
            </div>
          </div>

          <div
            className="ws-business-hero__profiles"
            aria-label={t("Profils accompagnés")}
          >
            <span className="ws-business-hero__label">
              {t("Choisissez votre profil")}
            </span>
            {profiles.map(({ icon: Icon, title, text }) => (
              <article key={title}>
                <span>
                  <Icon size={22} aria-hidden="true" />
                </span>
                <div>
                  <h2>{t(title)}</h2>
                  <p>{t(text)}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <PaymentFlowSection onStartRegistration={openRegistration} />

      <section
        className="ws-section ws-section--soft"
        aria-labelledby="ws-business-benefits-title"
      >
        <div className="ws-shell">
          <div className="ws-section-heading ws-section-heading--center">
            <span className="ws-kicker">{t("Pourquoi ce parcours ?")}</span>
            <h2 id="ws-business-benefits-title">
              {t("Une demande claire dès le départ")}
            </h2>
          </div>
          <div className="ws-business-benefits">
            {benefits.map(({ icon: Icon, title, text }) => (
              <article key={title}>
                <span>
                  <Icon size={23} aria-hidden="true" />
                </span>
                <h3>{t(title)}</h3>
                <p>{t(text)}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="ws-section">
        <div className="ws-shell ws-final-cta ws-business-final-cta">
          <div>
            <span className="ws-kicker">
              {t("Besoin d’aide avant de commencer ?")}
            </span>
            <h2>{t("Notre équipe peut vous orienter")}</h2>
            <p>
              {t(
                "Utilisez la page Contact pour une question générale, une intégration API ou un projet sur mesure.",
              )}
            </p>
          </div>
          <div className="ws-button-row ws-business-final-cta__actions">
            <button
              className="ws-button ws-button--primary"
              type="button"
              onClick={openRegistration}
              aria-haspopup="dialog"
            >
              {t("Commencer l’inscription")}
              <ArrowRight size={18} aria-hidden="true" />
            </button>
            <Link className="ws-button ws-button--secondary" to="/contact">
              {t("Contacter WonyaSoft")}{" "}
              <ArrowRight size={18} aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      <RegistrationModal
        isOpen={isRegistrationOpen}
        onClose={() => setIsRegistrationOpen(false)}
        triggerRef={registrationTriggerRef}
      />
    </div>
  );
};

export default Business;
